"use client"

import { useState } from "react"
import { useForm } from "@inertiajs/react"
import { Head, Link, router } from "@inertiajs/react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { toast } from 'sonner'
import { Upload, Wand2, ImageIcon, Video, Calendar, Send, Save, FileText, Link as LinkIcon, Settings } from "lucide-react"
import { CompleteEditor } from '@/components/blocks/editor-00/complete-editor'
import DashboardLayout from "@/layout/dashboard-layout"
import { ContentGeneratorModal } from "@/components/content-generator-modal"

interface Blog {
  id: number
  name: string
  domain: string
  active: boolean
}

interface Category {
  id: number
  name: string
}

interface Props {
  blogs: Blog[]
  categories: Category[]
}

export default function NovaPostagemPage({ blogs, categories }: Props) {
  const [selectedBlogs, setSelectedBlogs] = useState<number[]>([])
  const [isGeneratingContent, setIsGeneratingContent] = useState(false)
  const [isGeneratingImage, setIsGeneratingImage] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showContentGenerator, setShowContentGenerator] = useState(false)

  const { data, setData } = useForm({
    title: "",
    content: "",
    category_id: "",
    scheduled_at: "",
    destination: "home",
    media_url: "",
    ai_image_prompt: ""
  })

  const handleBlogSelection = (blogId: number, checked: boolean) => {
    if (checked) {
      setSelectedBlogs([...selectedBlogs, blogId])
    } else {
      setSelectedBlogs(selectedBlogs.filter((id) => id !== blogId))
    }
  }

  const generateContent = async () => {
    if (!data.title.trim()) {
      toast.warning('Título necessário', {
        description: 'Digite um título antes de gerar o conteúdo.',
        duration: 3000,
      })
      return
    }

    setIsGeneratingContent(true)
    
    try {
      const response = await fetch('/dashboard/posts/generate_content', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          context: data.title,
          character_limit: 1500,
          paragraph_count: 4,
          tone: 'journalistic',
          generate_title: false // Já temos o título
        })
      })

      const result = await response.json()

      if (result.success) {
        setData('content', result.content)
        toast.success('Conteúdo gerado com sucesso!', {
          description: `Gerado ${result.character_count} caracteres de conteúdo profissional.`,
          duration: 3000,
        })
      } else {
        throw new Error(result.errors?.join(', ') || 'Erro desconhecido')
      }
    } catch (error) {
      console.error('Erro ao gerar conteúdo:', error)
      toast.error('Erro ao gerar conteúdo', {
        description: error instanceof Error ? error.message : 'Tente novamente em alguns instantes.',
        duration: 5000,
      })
    } finally {
      setIsGeneratingContent(false)
    }
  }

  const handleContentGenerated = (content: string, title?: string) => {
    setData('content', content)
    if (title && !data.title.trim()) {
      setData('title', title)
    }
  }

  const generateImage = async () => {
    if (!data.ai_image_prompt.trim()) {
      toast.warning('Prompt necessário', {
        description: 'Digite um prompt para gerar a imagem.',
        duration: 3000,
      })
      return
    }

    setIsGeneratingImage(true)
    
    toast.promise(
      new Promise((resolve) => {
        setTimeout(() => {
          setIsGeneratingImage(false)
          resolve('success')
        }, 3000)
      }),
      {
        loading: 'Gerando imagem com DALL·E...',
        success: 'Imagem gerada com sucesso!',
        error: 'Erro ao gerar imagem.',
      }
    )
  }

  const handlePublishNow = async () => {
    if (!isFormValid) {
      toast.warning('Formulário incompleto', {
        description: 'Preencha todos os campos obrigatórios.',
        duration: 3000,
      })
      return
    }

    setIsPublishing(true)

    console.log('=== DEBUG PUBLISH NOW ===')
    console.log('selectedBlogs:', selectedBlogs)
    console.log('selectedBlogs length:', selectedBlogs.length)

    const postData = {
      title: data.title,
      content: data.content,
      category_id: parseInt(data.category_id),
      status: 'published',
      blog_ids: selectedBlogs
    }

    console.log('postData sendo enviado:', postData)

    router.post('/dashboard/posts', postData, {
      onSuccess: () => {
        toast.success('Post publicado com sucesso!', {
          description: `O post "${data.title}" foi publicado em ${selectedBlogs.length} blog(s).`,
          duration: 4000,
        })
      },
      onError: (errors: any) => {
        console.error('Erro ao publicar post:', errors)
        toast.error('Erro ao publicar post', {
          description: 'Verifique os dados e tente novamente.',
          duration: 4000,
        })
        setIsPublishing(false)
      }
    })
  }

  const handleSchedulePost = async () => {
    if (!isFormValid || !data.scheduled_at) {
      toast.warning('Dados incompletos', {
        description: 'Preencha todos os campos e defina a data de agendamento.',
        duration: 3000,
      })
      return
    }

    console.log('=== DEBUG SCHEDULE POST ===')
    console.log('selectedBlogs:', selectedBlogs)
    console.log('selectedBlogs length:', selectedBlogs.length)

    const postData = {
      title: data.title,
      content: data.content,
      category_id: parseInt(data.category_id),
      status: 'scheduled',
      scheduled_at: data.scheduled_at,
      blog_ids: selectedBlogs
    }

    console.log('postData sendo enviado (agendamento):', postData)

    router.post('/dashboard/posts', postData, {
      onSuccess: () => {
        toast.success('Post agendado com sucesso!', {
          description: `O post "${data.title}" foi agendado para ${new Date(data.scheduled_at).toLocaleString()}.`,
          duration: 4000,
        })
      },
      onError: (errors: any) => {
        console.error('Erro ao agendar post:', errors)
        toast.error('Erro ao agendar post', {
          description: 'Verifique os dados e tente novamente.',
          duration: 4000,
        })
      }
    })
  }

  const handleSaveDraft = async () => {
    if (!data.title.trim()) {
      toast.warning('Título necessário', {
        description: 'Digite pelo menos um título para salvar o rascunho.',
        duration: 3000,
      })
      return
    }

    setIsSaving(true)

    const postData = {
      title: data.title,
      content: data.content,
      category_id: data.category_id ? parseInt(data.category_id) : null,
      status: 'draft',
      blog_ids: selectedBlogs
    }

    router.post('/dashboard/posts', postData, {
      onSuccess: () => {
        toast.success('Rascunho salvo com sucesso!', {
          description: `O rascunho "${data.title}" foi salvo.`,
          duration: 4000,
        })
      },
      onError: (errors: any) => {
        console.error('Erro ao salvar rascunho:', errors)
        toast.error('Erro ao salvar rascunho', {
          description: 'Verifique os dados e tente novamente.',
          duration: 4000,
        })
        setIsSaving(false)
      }
    })
  }

  const isFormValid = data.title.trim() && data.content.trim() && selectedBlogs.length > 0 && data.category_id

  return (
    <DashboardLayout>
      <Head title="Nova Postagem" />
      
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Nova Postagem em Massa</h1>
          <p className="text-muted-foreground">Crie e publique conteúdo em múltiplos blogs simultaneamente</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Formulário Principal */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Conteúdo da Postagem
                </CardTitle>
                <CardDescription>Preencha as informações básicas da postagem</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="title">Título</Label>
                  <div className="flex gap-2">
                    <Input
                      id="title"
                      placeholder="Digite o título da postagem"
                      value={data.title}
                      onChange={(e) => setData('title', e.target.value)}
                      className="text-lg"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={generateContent}
                      disabled={!data.title || isGeneratingContent}
                      title="Gerar conteúdo rápido com IA"
                    >
                      <Wand2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setShowContentGenerator(true)}
                      title="Configurações avançadas de IA"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="content">Conteúdo</Label>
                    {isGeneratingContent && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        Gerando conteúdo com IA...
                      </div>
                    )}
                  </div>

                  <div className="min-h-[400px]">
                    <CompleteEditor
                      initialValue={data.content || ''}
                      onChange={(value) => setData('content', value)}
                      placeholder="Escreva o conteúdo do seu post aqui..."
                      content={data.content}
                    />
                  </div>
                </div>

                <Tabs defaultValue="upload" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="upload">Upload de Mídia</TabsTrigger>
                    <TabsTrigger value="ai-image">IA Imagem</TabsTrigger>
                    <TabsTrigger value="link">Link Externo</TabsTrigger>
                  </TabsList>

                  <TabsContent value="upload" className="space-y-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="mt-4 space-y-2">
                        <div className="flex justify-center gap-2">
                          <Button variant="outline" size="sm">
                            <ImageIcon className="mr-2 h-4 w-4" />
                            Selecionar Imagem
                          </Button>
                          <Button variant="outline" size="sm" className="bg-transparent">
                            <Video className="mr-2 h-4 w-4" />
                            Selecionar Vídeo
                          </Button>
                        </div>
                        <p className="text-sm text-gray-500">Ou arraste e solte arquivos aqui</p>
                        <p className="text-xs text-gray-400">Suporte: JPG, PNG, GIF, MP4, MOV (máx. 10MB)</p>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="ai-image" className="space-y-4">
                    <div className="space-y-3">
                      <Label>Prompt para geração de imagem</Label>
                      <Input
                        placeholder="Ex: Uma ilustração moderna sobre tecnologia, estilo minimalista, cores azul e branco..."
                        className="text-sm"
                        value={data.ai_image_prompt}
                        onChange={(e) => setData('ai_image_prompt', e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Descreva detalhadamente a imagem que deseja gerar com DALL·E
                      </p>
                    </div>
                    <Button onClick={generateImage} disabled={isGeneratingImage || !data.ai_image_prompt.trim()} className="w-full">
                      <Wand2 className="mr-2 h-4 w-4" />
                      {isGeneratingImage ? "Gerando Imagem..." : "Gerar Imagem com DALL·E"}
                    </Button>
                  </TabsContent>

                  <TabsContent value="link" className="space-y-4">
                    <div className="space-y-3">
                      <Label>URL da mídia</Label>
                      <Input 
                        placeholder="https://exemplo.com/imagem.jpg" 
                        type="url"
                        value={data.media_url}
                        onChange={(e) => setData('media_url', e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Cole o link direto para uma imagem ou vídeo hospedado externamente
                      </p>
                    </div>
                    <Button variant="outline" className="w-full bg-transparent">
                      <LinkIcon className="mr-2 h-4 w-4" />
                      Adicionar Link de Mídia
                    </Button>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Configurações e Seleção */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Selecionar Blogs</CardTitle>
                <CardDescription>Escolha onde publicar esta postagem</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {blogs.map((blog) => (
                  <div key={blog.id} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50">
                    <Checkbox
                      id={`blog-${blog.id}`}
                      checked={selectedBlogs.includes(blog.id)}
                      onCheckedChange={(checked) => handleBlogSelection(blog.id, checked as boolean)}
                      disabled={!blog.active}
                    />
                    <div className="flex-1">
                      <Label
                        htmlFor={`blog-${blog.id}`}
                        className={`text-sm font-medium cursor-pointer ${!blog.active ? "text-muted-foreground" : ""}`}
                      >
                        {blog.name}
                      </Label>
                      <Link href="/dashboard/posts" className="text-sm text-muted-foreground hover:underline">{blog.domain}</Link>
                    </div>
                    <Badge variant={blog.active ? "default" : "secondary"}>{blog.active ? "Ativo" : "Inativo"}</Badge>
                  </div>
                ))}

                {selectedBlogs.length > 0 && (
                  <div className="mt-3 p-2 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-700">{selectedBlogs.length} blog(s) selecionado(s)</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Configurações de Publicação</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Destino da Publicação</Label>
                  <Select value={data.destination} onValueChange={(value) => setData('destination', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="home">Página Inicial</SelectItem>
                      <SelectItem value="category">Categoria Específica</SelectItem>
                      <SelectItem value="both">Página Inicial + Categoria</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Select value={data.category_id} onValueChange={(value) => setData('category_id', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecionar categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id.toString()}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Agendamento</Label>
                  <Input 
                    type="datetime-local" 
                    value={data.scheduled_at}
                    onChange={(e) => setData('scheduled_at', e.target.value)}
                  />
                  <Link href="/dashboard/posts/scheduled" className="text-sm text-muted-foreground hover:text-foreground">Deixe em branco para publicar imediatamente</Link>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-3">
              <Button 
                className="w-full h-12" 
                disabled={!isFormValid || isPublishing}
                onClick={handlePublishNow}
              >
                {isPublishing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Publicando...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Publicar Agora
                  </>
                )}
              </Button>
              <Button 
                variant="outline" 
                className="w-full h-12 bg-transparent"
                onClick={handleSchedulePost}
                disabled={!isFormValid || !data.scheduled_at}
              >
                <Calendar className="mr-2 h-4 w-4" />
                Agendar Publicação
              </Button>
              <Button 
                variant="ghost" 
                className="w-full h-12"
                onClick={handleSaveDraft}
                disabled={!data.title.trim() || isSaving}
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvar Rascunho
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ContentGeneratorModal
        isOpen={showContentGenerator}
        onClose={() => setShowContentGenerator(false)}
        onGenerate={handleContentGenerated}
        initialContext={data.title}
      />
    </DashboardLayout>
  )
}
